<div class="settings-provider-tab d-flex flex-column gap-3">
    <div class="card bcx-card bcx-card-accent border bg-white shadow-sm">
        <div class="card-body p-4">
            <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                <div>
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h4 class="fw-bold mb-0">Service Provider Assignments</h4>
                        <span class="badge rounded-pill settings-soft-badge settings-soft-badge-info">
                            {{ assigned_feed_count }} of {{ provider_feeds.length }} feeds assigned
                        </span>
                    </div>
                    <p class="text-secondary text-sm mb-0">
                        Manage the data and specialist service providers used by each property dashboard.
                    </p>
                </div>

                <div class="d-flex flex-column flex-sm-row align-items-sm-end gap-2">
                    <div class="settings-provider-property-select">
                        <label for="service-provider-property" class="form-label text-secondary text-xs fw-semibold mb-2">
                            Property
                        </label>
                        <div class="settings-provider-select-wrap">
                            <select
                                id="service-provider-property"
                                v-model="selected_property_id"
                                class="form-select form-control text-sm settings-provider-select"
                            >
                                <option v-for="property in active_properties" :key="property.id" :value="property.id">
                                    {{ property.name }}
                                </option>
                            </select>
                            <i class="bi bi-chevron-down settings-provider-select-chevron" aria-hidden="true"></i>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="btn btn-primary settings-button-shape px-4"
                        :disabled="!selected_property"
                        @click="save_assignments"
                    >
                        <i class="bi bi-check2-circle"></i>
                        Save assignments
                    </button>
                </div>
            </div>

            <p v-if="saved_message" class="settings-provider-saved text-xs mt-3 mb-0">{{ saved_message }}</p>
            <p v-else class="text-secondary text-xs mt-3 mb-0">
                {{ selected_property ? `Ready to save configuration for ${selected_property.name}.` : "Select a property to save provider configuration." }}
            </p>
        </div>
    </div>

    <div v-if="!active_properties.length" class="card bcx-card border bg-white shadow-sm text-center">
        <div class="card-body py-5">
            <i class="bi bi-building text-secondary fs-3"></i>
            <p class="fw-semibold mt-3 mb-1">No active properties available</p>
            <p class="text-secondary text-xs mb-0">Active properties will appear here once loaded.</p>
        </div>
    </div>

    <template v-else>
        <div class="row g-3 align-items-stretch">
            <div class="col-12 col-xl-8">
                <div class="card bcx-card border bg-white shadow-sm overflow-hidden h-100">
                    <div class="card-header bg-white border-bottom p-4">
                        <div class="d-flex align-items-start gap-3">
                            <span class="settings-provider-section-icon d-inline-flex align-items-center justify-content-center rounded-3">
                                <i class="bi bi-database-fill"></i>
                            </span>
                            <div>
                                <h5 class="fw-bold mb-1">Source Feed Providers</h5>
                                <p class="text-secondary text-xs mb-0">
                                    Select providers only for the raw utility and energy feeds.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="card-body p-4">
                        <div class="d-flex flex-column gap-3">
                            <div
                                v-for="feed in provider_feeds"
                                :key="feed.id"
                                class="settings-provider-row border rounded-2 p-3"
                            >
                                <div class="row g-3 align-items-center">
                                    <div class="col-12 col-lg-5">
                                        <div class="d-flex align-items-center gap-3">
                                            <span class="settings-provider-dashboard-icon d-inline-flex align-items-center justify-content-center rounded-3">
                                                <i :class="feed.icon"></i>
                                            </span>
                                            <div class="min-w-0">
                                                <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                                                    <p class="fw-semibold text-sm mb-0">{{ feed.dashboard }}: {{ feed.title }}</p>
                                                    <span class="badge rounded-pill settings-soft-badge text-xxs">Source feed</span>
                                                </div>
                                                <p class="text-secondary text-xs mb-0">{{ feed.description }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12 col-lg-7">
                                        <label :for="`provider-${feed.id}`" class="form-label text-secondary text-xxs text-uppercase fw-bold mb-2">
                                            Data provider
                                        </label>
                                        <div class="settings-provider-select-wrap">
                                            <select
                                                :id="`provider-${feed.id}`"
                                                :value="get_feed_provider_id(feed.id)"
                                                class="form-select form-control text-xs settings-provider-select"
                                                @change="set_feed_provider(feed.id, $event.target.value)"
                                            >
                                                <option v-for="provider in get_feed_provider_options(feed.id)" :key="provider.id" :value="provider.id">
                                                    {{ provider.title }}
                                                </option>
                                            </select>
                                            <i class="bi bi-chevron-down settings-provider-select-chevron" aria-hidden="true"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 col-xl-4">
                <div class="card bcx-card border bg-white shadow-sm overflow-hidden h-100">
                    <div class="card-header bg-white border-bottom p-4">
                        <div class="d-flex align-items-start gap-3">
                            <span class="settings-provider-section-icon d-inline-flex align-items-center justify-content-center rounded-3">
                                <i class="bi bi-lock-fill"></i>
                            </span>
                            <div>
                                <h5 class="fw-bold mb-1">Dedicated Providers</h5>
                                <p class="text-secondary text-xs mb-0">These dashboards use a fixed specialist provider.</p>
                            </div>
                        </div>
                    </div>

                    <div class="card-body p-4 d-flex flex-column">
                        <div class="d-flex flex-column gap-3">
                            <div
                                v-for="dashboard in fixed_dashboards"
                                :key="dashboard.id"
                                class="settings-provider-fixed-card border rounded-2 p-3"
                            >
                                <div class="d-flex align-items-center gap-3 mb-3">
                                    <span class="settings-provider-dashboard-icon d-inline-flex align-items-center justify-content-center rounded-3">
                                        <i :class="dashboard.icon"></i>
                                    </span>
                                    <div class="min-w-0">
                                        <p class="fw-semibold text-sm mb-1">{{ dashboard.title }}</p>
                                        <p class="text-secondary text-xs mb-0">{{ dashboard.description }}</p>
                                    </div>
                                </div>
                                <div class="d-flex justify-content-between align-items-center gap-2">
                                    <span class="text-secondary text-xs">Assigned provider</span>
                                    <span class="badge rounded-pill settings-soft-badge">{{ dashboard.provider }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="border-top mt-4 pt-4">
                            <p class="fw-semibold text-sm mb-2">Derived dashboards</p>
                            <p class="text-secondary text-xs mb-3">
                                These dashboards combine the source feeds automatically. No provider selection is needed.
                            </p>
                            <div class="d-flex flex-column gap-2">
                                <div
                                    v-for="dashboard in derived_dashboards"
                                    :key="dashboard.id"
                                    class="d-flex align-items-center gap-2 text-secondary text-xs"
                                >
                                    <i :class="dashboard.icon"></i>
                                    <span>{{ dashboard.title }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="border-top mt-4 pt-4 mt-auto">
                            <p class="text-secondary text-xs mb-2">Selected property</p>
                            <p class="fw-semibold text-sm mb-0">{{ selected_property?.name || "No property selected" }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>
</div>
